/**
 * @module use-cases/user/slice
 * @desc User Slice (Reducers and ExtraReducers)
 *
 */

import { createSlice } from 'core/use-cases/utilities';
import { actions as actionsCart } from 'core/use-cases/cart';
import { Utilities } from 'core/domains';
import { Cookies } from 'core/utilities';

import * as actions from './actions';
import { initialState } from './initial-state';
import { getNewCycle } from '../helpers';

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(actions.userResetState, () => initialState);
    builder.addCase(actions.userResaleIntention.request, (state) => {
      state.status = Utilities.STATUS.RUNNING;
    });
    builder.addCase(actions.userResaleIntention.failure, (state) => {
      state.status = Utilities.STATUS.ERROR;
    });
    builder.addCase(actions.userResaleIntention.success, (state, { payload }) => {
      state.data.isResaleIntention = payload?.isResaleIntention ?? false;
      state.data.isAutoStartEnabled = payload?.isAutoStartEnabled ?? false;

      state.status = Utilities.STATUS.SUCCESS;
    });

    builder.addCase(actions.cyclesFetch.request, (state) => {
      state.status = Utilities.STATUS.RUNNING;
    });

    builder.addCase(actions.cyclesFetch.failure, (state) => {
      state.status = Utilities.STATUS.ERROR;
    });

    builder.addCase(actions.cyclesFetch.success, (state, { payload, meta }) => {
      state.data.avaliableCycles = payload;
      state.status = Utilities.STATUS.SUCCESS;
      state.updatedAt = meta?.updatedAt;

      if (
        payload.canChooseCycle &&
        localStorage.getItem(`choose-new-cycle-${state.data.resellerId}`)
      ) {
        state.data.cycle = getNewCycle(
          state.data.cycle.nextCycle,
          state.data.avaliableCycles.cycles,
        );
      }
    });

    builder.addCase(actions.creditFetch.request, (state) => {
      state.credit.message = '';
      state.credit.status = Utilities.STATUS.RUNNING;
    });

    builder.addCase(actions.creditFetch.success, (state, { meta, payload }) => {
      state.credit.data = payload;
      state.credit.status = Utilities.STATUS.SUCCESS;
      state.credit.updatedAt = meta?.updatedAt;
    });

    builder.addCase(actions.creditFetch.failure, (state, { error }) => {
      const { message } = error;

      state.credit.message = message;
      state.credit.status = Utilities.STATUS.ERROR;
    });

    builder.addCase(actions.userFetch.request, (state) => {
      state.message = '';
      state.status = Utilities.STATUS.RUNNING;
    });

    builder.addCase(actions.userFetch.success, (state, { meta, payload }) => {
      const newPayload = {
        ...payload,
        isResaleIntention: state.data.isResaleIntention,
        isAutoStartEnabled: state.data.isAutoStartEnabled,
      };
      state.data = newPayload;
      state.status = Utilities.STATUS.SUCCESS;
      state.updatedAt = meta?.updatedAt;
    });

    builder.addCase(actions.userFetch.failure, (state, { error }) => {
      const { message } = error!;

      state.message = message;
      state.status = Utilities.STATUS.ERROR;
    });

    builder.addCase(actions.changeCycle.request, (state) => {
      state.data.avaliableCycles.status = Utilities.STATUS.RUNNING;
    });

    builder.addCase(actions.changeCycle.success, (state) => {
      state.data.cycle = getNewCycle(state.data.cycle.nextCycle, state.data.avaliableCycles.cycles);
      state.data.avaliableCycles.status = Utilities.STATUS.SUCCESS;
      state.data.avaliableCycles.message = 'Você mudou de ciclo com sucesso!';
    });

    builder.addCase(actions.changeCycle.failure, (state, { error }) => {
      const { response, message } = error;

      state.data.avaliableCycles.status = Utilities.STATUS.ERROR;
      state.data.avaliableCycles.message = response?.errorId ?? message;
    });

    builder.addCase(actions.goToExtendedCycleSuccess, (state) => {
      state.data.avaliableCycles.status = Utilities.STATUS.SUCCESS;
      state.data.avaliableCycles.message =
        'Sucesso! Você está no ciclo estendido e poderá ficar nele por mais 2 dias';
    });

    builder.addCase(actions.updateCycle, (state) => {
      state.data.cycle = getNewCycle(state.data.cycle.nextCycle, state.data.avaliableCycles.cycles);
      Cookies.set('currentCycle', `${state.data.cycle.nextCycle}`, { domain: '.boticario.com.br' });
    });

    builder.addCase(actions.setSearchInput, (state, { payload = '' }) => {
      state.shippingModelSearch = payload;
    });

    builder.addCase(actions.userBuyIntentionFetch.request, (state) => {
      state.buyIntention.message = '';
      state.buyIntention.status = Utilities.STATUS.RUNNING;
    });

    builder.addCase(actions.userBuyIntentionFetch.success, (state, { meta: { payload } }) => {
      if (payload) {
        const { productCode } = payload;

        const hasProductCode = state.buyIntention.items.includes(productCode);

        if (!hasProductCode) {
          state.buyIntention.items = [...state.buyIntention.items, productCode];
        }

        state.buyIntention.message = '';
        state.buyIntention.status = Utilities.STATUS.SUCCESS;
      }
    });

    builder.addCase(actions.userBuyIntentionFetch.failure, (state, { error }) => {
      const { message } = error!;
      state.buyIntention.message = message;
      state.buyIntention.status = Utilities.STATUS.ERROR;
    });

    builder.addCase(actionsCart.itemAddFetch.failure, (state, { meta }) => {
      if (meta && meta.payload) {
        const { productCode } = meta.payload;

        const hasProductCode = state.itemAddCartError.includes(productCode);

        if (!hasProductCode) {
          state.itemAddCartError = [...state.itemAddCartError, productCode];
        }
      }
    });

    builder.addCase(actions.incentiveCampaignsListFetch.request, (state) => {
      state.incentiveCampaigns.message = '';
      state.incentiveCampaigns.status = Utilities.STATUS.RUNNING;
    });

    builder.addCase(actions.incentiveCampaignsListFetch.success, (state, { payload }) => {
      state.incentiveCampaigns.data = payload;
      state.incentiveCampaigns.message = '';
      state.incentiveCampaigns.status = Utilities.STATUS.SUCCESS;
    });

    builder.addCase(actions.incentiveCampaignsListFetch.failure, (state, { error }) => {
      const { message } = error;

      state.incentiveCampaigns.message = message;
      state.incentiveCampaigns.status = Utilities.STATUS.ERROR;
    });
  },
});

export const { reducer } = userSlice;
export { actions };
